import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
// import Swal from "sweetalert2";
import instance, { BASE_URL, IMAGE_URL } from "../../services/api";
import toast from "react-hot-toast";

export const image_url = IMAGE_URL;

const formatOnboardingError = (error) => {
  const data = error?.response?.data;

  if (!data) {
    if (error?.message === "Network Error") {
      return "Unable to reach the server. Check your connection and try again.";
    }
    return error?.message || "Failed to submit onboarding form.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.detail) {
    return typeof data.detail === "string"
      ? data.detail
      : JSON.stringify(data.detail);
  }

  if (data.message) {
    return data.message;
  }

  if (data.error) {
    return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
  }

  const fieldMessages = Object.entries(data)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(", ")}`;
      }
      if (typeof value === "object" && value !== null) {
        return `${field}: ${JSON.stringify(value)}`;
      }
      return `${field}: ${value}`;
    })
    .filter(Boolean);

  if (fieldMessages.length > 0) {
    return fieldMessages.join(" ");
  }

  return "Failed to submit onboarding form.";
};

const AuthViewController = () => {
  const navigate = useNavigate();

  const login = (requestData) => async (dispatch) => {
    dispatch({ type: "LOADING", payload: true });

    try {
      const response = await axios.post(`${BASE_URL}login/`, requestData);

      const { message, user_id, access, refresh, permissions } = response.data;

      if (!access) {
        throw new Error("Access token missing in response");
      }

      // Decode token to extract user_type or any custom claims
      let decodedToken;
      try {
        decodedToken = jwtDecode(access);
      } catch (decodeError) {
        console.error("Invalid Token:", decodeError);
        throw new Error("Invalid token format, login failed");
      }

      // const user_type = decodedToken?.user_type || "";
      // console.log("user_type",user_type);

      // setAuthToken(access);
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);
      localStorage.setItem("user_id", String(user_id));
      localStorage.setItem("user_type", decodedToken?.user_type || "");

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { access, user_id, refresh, permissions }
      });
      
      dispatch({ type: "LOADING", payload: false });
      toast.success("You are successfully logged in");

      navigate("/");
    } catch (error) {
      dispatch({ type: "LOADING", payload: false });

      toast.error(
        error.response?.data?.non_field_errors?.[0] ||
          "Something went wrong. Please try again!"
      );


      dispatch({
        type: "LOGIN_FAILURE",
        payload: {
          error:
            error.response?.data?.non_field_errors?.[0] || error?.response?.data
        }
      });
    }
  };

  const activeAccount =
    ({ uid, token }) =>
    async (dispatch) => {
      dispatch({ type: "LOADING", payload: true });

      try {
        const response = await axios.get(
          `${BASE_URL}activate/${uid}/${encodeURIComponent(token)}/`
        );

        const jsonString = JSON.stringify(response.data);
        const data = JSON.parse(jsonString);
        dispatch({ type: "ACCOUNT_ACTIVATION_SUCCESS", payload: data });
        dispatch({ type: "LOADING", payload: false });
      } catch (error) {
        dispatch({ type: "LOADING", payload: false });
        dispatch({
          type: "ACCOUNT_ACTIVATION_FAILURE",
          payload: { error: error }
        });
      }
    };

  const resetPassword = (uid, token, reqData) => async (dispatch) => {
    dispatch({ type: "LOADING", payload: true });
    try {
      const response = await axios.post(
        `${BASE_URL}reset-password/${uid}/${encodeURIComponent(token)}/`,
        reqData
      );

      const jsonString = JSON.stringify(response.data);
      const data = JSON.parse(jsonString);
      dispatch({ type: "PASSWORD_RESET_SUCCESS", payload: data });
      dispatch({ type: "LOADING", payload: false });
      
      // Clear any existing tokens so the user isn't automatically logged in
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("user_type");
      
      toast.success("Password reset successfully!");
      navigate("/login");
    } catch (error) {
      dispatch({ type: "LOADING", payload: false });
      dispatch({
        type: "PASSWORD_RESET_FAILURE",
        payload: { error: error }
      });
      
      let errorMsg = "Failed to reset password. Please try again.";
      if (error.response?.data) {
        const data = error.response.data;
        if (Array.isArray(data.detail)) errorMsg = data.detail[0];
        else if (typeof data.detail === 'string') errorMsg = data.detail;
        else if (Array.isArray(data.password)) errorMsg = data.password[0];
        else if (Array.isArray(data.non_field_errors)) errorMsg = data.non_field_errors[0];
      }
      toast.error(errorMsg);
      throw error;
    }
  };

  const submitOnboardingForm =
    (uid, token, formData, navigate) => async (dispatch) => {
      dispatch({ type: "LOADING", payload: true });

      try {
        const response = await axios.post(
          `${BASE_URL}onboarding-form/${uid}/${encodeURIComponent(token)}/`,
          formData
        );

        dispatch({ type: "ADD_FORM_DATA_SUCCESS", payload: response.data });
        dispatch({ type: "LOADING", payload: false });
        toast.success(
          response.data?.detail || "Onboarding submitted successfully."
        );
        
        // Clear any existing tokens so the user is forced to log in fresh
        // This is especially useful if an admin/employer was testing the link in the same browser
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("user_type");
        
        navigate("/login");
        return response.data;
      } catch (error) {
        const message = formatOnboardingError(error);

        if (process.env.NODE_ENV !== "production") {
          console.error("Onboarding submit failed:", {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          });
        }

        dispatch({ type: "LOADING", payload: false });
        dispatch({
          type: "ADD_FORM_DATA_FAILURE",
          payload: { error: error.response?.data || error.message },
        });
        toast.error(message);
        throw error;
      }
    };

  const getUserDetails = () => async (dispatch) => {
    dispatch({ type: "LOADING", payload: true });
    try {
      const response = await instance.get(
        `${BASE_URL}user-profile/`
      );

      const jsonString = JSON.stringify(response.data);
      const data = JSON.parse(jsonString);
      dispatch({ type: "GET_USER_DETAILS_SUCCESS", payload: data });
      dispatch({ type: "LOADING", payload: false });
    } catch (error) {
      dispatch({ type: "LOADING", payload: false });
      dispatch({
        type: "GET_USER_DETAILS_FAILURE",
        payload: { error: error }
      });
    }
  };

   const getDashboardData = () => async (dispatch) => {
    dispatch({ type: "LOADING", payload: true });
    try {
      const response = await instance.get(
        `${BASE_URL}employee/dashboard/`
      );

      const jsonString = JSON.stringify(response.data);
      const data = JSON.parse(jsonString);
      dispatch({ type: "GET_DASHBOARD_DATA_SUCCESS", payload: data });
      dispatch({ type: "LOADING", payload: false });
      return data;
    } catch (error) {
      dispatch({ type: "LOADING", payload: false });
      dispatch({
        type: "GET_DASHBOARD_DATA_FAILURE",
        payload: { error: error }
      });
    }
  };

  const getAdminDashboardData = () => async (dispatch) => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return;
    }

    dispatch({ type: "LOADING", payload: true });
    try {
      const response = await instance.get(`${BASE_URL}employer/dashboard/`);
      dispatch({
        type: "GET_ADMIN_DASHBOARD_DATA_SUCCESS",
        payload: response.data,
      });
      dispatch({ type: "LOADING", payload: false });
    } catch (error) {
      dispatch({ type: "LOADING", payload: false });
      const status = error.response?.status;
      const detail =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message;

      if (status === 403) {
        toast.error("You do not have permission to view this dashboard.");
      } else if (status !== 401) {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Failed to load dashboard data. Please try again."
        );
      }

      dispatch({
        type: "GET_ADMIN_DASHBOARD_DATA_FAILURE",
        payload: { error: error },
      });
    }
  };

  const getEmployerDashboardData = () => async (dispatch) => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return;
    }

    dispatch({ type: "LOADING", payload: true });
    try {
      const response = await instance.get(`${BASE_URL}employer/dashboard/`);
      dispatch({
        type: "GET_EMPLOYER_DASHBOARD_DATA_SUCCESS",
        payload: response.data,
      });
      dispatch({ type: "LOADING", payload: false });
    } catch (error) {
      dispatch({ type: "LOADING", payload: false });
      const status = error.response?.status;
      const detail =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message;

      if (status === 403) {
        toast.error("You do not have permission to view this dashboard.");
      } else if (status !== 401) {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Failed to load dashboard data. Please try again."
        );
      }

      dispatch({
        type: "GET_EMPLOYER_DASHBOARD_DATA_FAILURE",
        payload: { error: error },
      });
    }
  };
  
  const getUserPermissions = () => async (dispatch) => {
    dispatch({ type: "LOADING", payload: true });
    try {
      const response = await instance.get(
        `${BASE_URL}user/permissions/`
      );

      const jsonString = JSON.stringify(response.data);
      const data = JSON.parse(jsonString);
      dispatch({ type: "GET_USER_PERMISSION_SUCCESS", payload: data });
      dispatch({ type: "LOADING", payload: false });
    } catch (error) {
      console.log(
        "Error fetching user permissions:",
        error.data || error.message
      );

      dispatch({ type: "LOADING", payload: false });
      dispatch({
        type: "GET_USER_PERMISSION_FAILURE",
        payload: { error: error }
      });
    }
  };
  const logout = () => (dispatch) => {
    localStorage.clear();
    // setAuthToken(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_type");

    dispatch({ type: "LOGOUT" });
    navigate("/login");
  };

  return {
    login,
    logout,
    getUserDetails,
    getDashboardData,
    activeAccount,
    resetPassword,
    submitOnboardingForm,
    getEmployerDashboardData,
    getAdminDashboardData,
    getUserPermissions
  };
};

export default AuthViewController;
